<template>
  <div
    ref="container"
    class="chart-sql-editor">
    <div class="chart-sql-editor__container">
      <div class="chart-sql-editor__menu">
        <SidebarMenu />
      </div>

      <div class="chart-sql-editor__data">
        <header class="chart-sql-editor__data-header flex mb-24" v-if="closeLinkCopy">
          <a
            @click.prevent="$emit('back')"
            class="btn btn--xs btn--secondary btn--with-icon"
            href="#">
            <LeftArrowIcon class="btn__icon" />
            {{ closeLinkCopy }}
          </a>
        </header>

        <div class="chart-sql-editor__query-editor">
        </div>

        <div class="chart-sql-editor__actions flex align-start mb-48">
          <button
            class="chart-sql-editor__run-query btn btn--sm"
            :disabled="formSubmitted || !databasesLoaded || query.length === 0"
            @click.prevent="submitQuery">

            <span v-if="formSubmitted">
              Running query...
            </span>

            <span v-else>
              RUN QUERY
            </span>
          </button>

          <div class="chart-sql-editor__secondary-actions flex align-center">
            <a
              href="#"
              class="flex align-center font-small no-underline grey-5 ml-16"
              @click.prevent="setRandomQuery"
              v-if="showRandomQueryLink && databases.length > 0">
              <RandomIcon class="w-12 h-12 mr-4" />
              Random query
            </a>
          </div>
        </div>

        <div
          v-if="showResultAndChart"
          class="chart-sql-editor__editor-label font-section mb-16">
          QUERY RESULT
        </div>

        <div
          class="chart-sql-editor__query-result mb-48"
          v-if="(this.queryResult || this.queryErrors) && showResultAndChart">

          <QueryResultWrapper
            :showHeadline="false"
            :maxHeight="320"
            :fields="queryFields"
            :result="queryResult"
            :duration="queryDuration"
            :errors="queryErrors" />
        </div>

        <div
          v-if="showResultAndChart"
          class="chart-sql-editor__editor-label font-section mb-16">
          CHART
        </div>

        <div
          class="chart-sql-editor__chart-editor"
          v-if="queryResult && showResultAndChart">

          <ChartEditor />
        </div>
      </div>

      <div class="chart-sql-editor__config pt-24">
        <ChartConfig />
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import ace from "ace-builds/src-noconflict/ace";
import "ace-builds/src-noconflict/ext-language_tools";
import "ace-builds/src-noconflict/mode-pgsql";
import "ace-builds/src-noconflict/theme-sqlserver";
import { notify } from "modules/notify";
import QueryResultWrapper from "components/shared/query-result-wrapper";
import ChartEditor from "components/chart/chart-editor";
import ChartConfig from "components/chart/chart-config";
import Header from "components/chart/header";
import log from "modules/log";
import Checkbox from "components/shared/checkbox";
import RandomIcon from "@images/font_awesome/redo-light.svg";
import LeftArrowIcon from "@images/font_awesome/long-arrow-left-light.svg";
import SidebarMenu from "components/chart/sidebar-menu";
import PGSQL_KEYWORDS from "modules/ace-pgsql-keywords";
import Sentry from "modules/sentry";

import { mapGetters } from "vuex";

export default {
  components: {
    QueryResultWrapper,
    ChartEditor,
    ChartConfig,
    Header,
    Checkbox,
    RandomIcon,
    SidebarMenu,
    LeftArrowIcon
  },
  data() {
    return {
      query: "",
      formSubmitted: false,
      queryFields: null,
      queryResult: null,
      queryDuration: null,
      queryErrors: null,
      editor: null,
      showResultAndChart: false
    }
  },
  props: {
    interviewItem: {
      type: Object
    },
    showRandomQueryLink: {
      type: Boolean,
      default: true
    },
    closeLinkCopy: {
      type: String,
      default: null
    },
    inputQuery: {
      type: String,
      default: null
    },
    inputDatasetId: {
      type: Number,
      default: null
    }
  },
  watch: {
    inputQuery() {
      this.setInputQuery();
    },
    inputDatasetId() {
      this.setInputQuery();
    }
  },
  computed: {
    ...mapGetters("chart", ["currentDatabaseId", "databases"]),
    ...mapGetters("user", ["isGuest"]),
    databasesLoaded() {
      return this.currentDatabaseId !== null;
    }
  },
  methods: {
    clearQuery() {
      this.editor.getSession().setValue("");
      this.queryResult = null;
      this.queryDuration = null;
    },
    fetchDatasets() {
      axios.get("/api/datasets.json", {}, {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
        }
      }).then(response => {
        this.$store.dispatch("chart/setDatabases", {
          databases: response.data.datasets,
          currentDatabaseId: this.inputDatasetId
        });
      }).catch((error) => {
        notify({
          message: error.response.data.errors,
          type: "error"
        });
      });
    },
    setRandomQuery() {
      const queries = [{
        query: `-- Signups count by country by day\n\nSELECT \n  signup_date,\n  country,\n  COUNT(*) AS users_count\nFROM users\nGROUP BY 1, 2\nORDER BY 1 ASC`,
        databaseName: "Bindle dataset"
      }, {
        query: `-- Number of organic vs paid signups\n\nSELECT \n  CASE WHEN utm_campaign <> '' THEN 'paid' ELSE 'organic' END AS user_type,\n  COUNT(*) AS users_count\nFROM users\nGROUP BY 1`,
        databaseName: "Bindle dataset"
      }, {
        query: `-- Cumulative signup count by month\n\nWITH signups_by_month AS (\n  SELECT\n    DATE_TRUNC('month', created_at) AS m,\n    COUNT(*) AS signups_count\n  FROM users\n  GROUP BY 1\n  ORDER BY 1 ASC\n)\n\nSELECT\n  m,\n  SUM(signups_count) OVER (ORDER BY m ASC) AS cumulative_signups_count\nFROM signups_by_month`,
        databaseName: "Bindle dataset"
      }, {
        query: `-- Daily organic vs paid signups count\n\nSELECT \n  CASE WHEN utm_campaign <> '' THEN 'paid' ELSE 'organic' END AS user_type,\n  signup_date,\n  COUNT(*) AS users_count\nFROM users\nGROUP BY 1, 2\nORDER BY 1, 2 ASC`,
        databaseName: "Bindle dataset"
      }, {
        query: `-- Revenue last 14 days\n\nSELECT\n  CASE WHEN refunded_at IS NULL THEN 'paid' ELSE 'refunded' END AS purchase_type,\n  created_at::date AS d,\n  SUM(amount) AS total_amount\nFROM purchases\nWHERE\n  created_at > now() - '2 weeks'::INTERVAL\nGROUP BY 1, 2\nORDER BY 2 ASC`,
        databaseName: "Live dataset"
      }, {
        query: `-- TOP-10 vendors by revenue\n\nSELECT\n  name,\n  ROUND(SUM(amount_usd), 2) AS revenue_usd\nFROM transactions t \nINNER JOIN vendors v\n  ON t.vendor_id = v.id\nWHERE\n  amount_usd > 0 -- credit transactions\nGROUP BY 1\nORDER BY 2 DESC\nLIMIT 10`,
        databaseName: "Finance dataset"
      },{
        query: `-- Daily trial opt-in and purchase rates\n\nSELECT\n  u.created_at::date AS d,\n  100.0 * COUNT(DISTINCT(t.user_id)) / COUNT(DISTINCT(u.id)) AS trial_optin_rate,\n  100.0 * COUNT(DISTINCT(p.user_id)) / COUNT(DISTINCT(u.id)) AS purchase_rate\nFROM users u\nLEFT JOIN trials t\n  ON u.id = t.user_id\nLEFT JOIN purchases p\n  ON u.id = p.user_id\nWHERE\n  u.created_at > now() - '30 days'::INTERVAL\nGROUP BY 1`,
        databaseName: "Live dataset"
      }];

      const query = queries[Math.floor(Math.random() * queries.length)];

      this.editor.getSession().setValue(query.query);

      const database = this.databases.find(d => d.name === query.databaseName);

      this.$store.dispatch("chart/clearState").then(() => {
        this.$store.dispatch("chart/setCurrentDatabaseId", database.id).then(() => {
          this.submitQuery();
        })
      });
    },
    setInputQuery() {
      if(this.inputQuery && this.inputDatasetId) {
        this.editor.getSession().setValue(this.inputQuery);
        this.query = this.inputQuery;

        this.$store.dispatch("chart/setCurrentDatabaseId", this.inputDatasetId).then(() => {
          this.$nextTick(() => {
            this.submitQuery();
          });
        })
      }
    },
    submitQuery() {
      if(!window.state.loggedIn) {
        return;
      }

      if(this.query.length === 0) {
        return;
      }

      if(this.formSubmitted) {
        return;
      } else {
        this.formSubmitted = true;
      }

      let formData = new FormData();

      formData.append("result_format", "json");
      formData.append("query[body]", this.query);
      formData.append("query[dataset_id]", this.currentDatabaseId);

      if(window.state && window.state.contentItem) {
        formData.append("query[content_id]", window.state.contentItem.contentId);
        formData.append("query[content_type]", window.state.contentItem.contentType);
      }

      axios.post("/queries.json", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
        }
      }).then(response => {
        this.showResultAndChart = true;

        this.queryFields = response.data.fields;
        this.queryResult = response.data.result;
        this.queryDuration = response.data.duration;
        this.queryErrors = response.data.errors;

        this.$store.dispatch("chart/setData", {
          fields: response.data.fields,
          result: response.data.result,
          fieldTypes: response.data.types
        });
      }).catch((error) => {
        log(error);

        const errorMessage = "Something went wrong, please try again or ping support@sqlhabit.com";

        notify({
          message: errorMessage,
          type: "error"
        });

        this.queryFields = null;
        this.queryResult = null;
        this.queryErrors = errorMessage;

        Sentry.captureException(error);
      }).finally(() => {
        setTimeout(() => {
          this.formSubmitted = false;
        }, 1000);
      });
    }
  },
  beforeMount() {
    this.fetchDatasets();
  },
  mounted() {
    let $editorContainer = document.querySelector(".chart-sql-editor__query-editor");

    this.editor = ace.edit($editorContainer);

    this.editor.setOptions({
      fontFamily: "Hack",
      enableBasicAutocompletion: true,
      enableLiveAutocompletion: true,
      enableSnippets: false,
      highlightActiveLine: false,
      placeholder: "👉 Start typing SQL query here..."
    });

    if (!("state" in window)) {
      window.state = {};
    }

    window.state.editor = this.editor;

    const schemaAutoCompleter = {
      getCompletions: (editor, session, pos, prefix, callback) => {
        if (prefix.length === 0) {
          callback(null, []); return
        }

        axios.get(
          `/queries/autocomplete.json?q=${prefix}&dataset_id=${this.currentDatabaseId}`
        ).then(response => {
          callback(null, response.data.words.map(function(ea) {
            return {
              name: ea.word,
              value: ea.word,
              score: ea.score,
              meta: ea.meta
            }
          }));
        }).catch(function(error) {
          Sentry.captureException(error);
        });
      }
    };

    const sqlAutoCompleter = {
      getCompletions: (editor, session, pos, prefix, callback) => {
        if (prefix.length === 0) {
          callback(null, []); return
        }

        let matchedKeywords = Object.keys(PGSQL_KEYWORDS).reduce((a, key) => {
          let re = new RegExp(prefix, "i");

          if(key.match(re)) {
            if(typeof(PGSQL_KEYWORDS[key]) === "string") {
              a.push({
                name: key,
                value: key,
                meta: PGSQL_KEYWORDS[key]
              });
            } else if(typeof(PGSQL_KEYWORDS[key]) === "object") {
              let params = {
                name: key,
                caption: PGSQL_KEYWORDS[key].caption || key,
                value: PGSQL_KEYWORDS[key].value,
                meta: PGSQL_KEYWORDS[key].type
              };

              if(PGSQL_KEYWORDS[key].completer) {
                params.completer = PGSQL_KEYWORDS[key].completer;
              }

              a.push(params);
            }
          }

          return a;
        }, []);

        callback(null, matchedKeywords);
      }
    };

    this.editor.completers = [
      schemaAutoCompleter,
      sqlAutoCompleter
    ];

    this.editor.setTheme("ace/theme/sqlserver");
    this.editor.setShowPrintMargin(false);
    this.editor.session.setMode("ace/mode/pgsql");

    if(screen.width <= 768) {
      this.editor.setFontSize("18px");
    }

    let newQuery;
    this.editor.getSession().on("change", () => {
      newQuery = this.editor.getSession().getValue();
      this.query = newQuery;

      this.$emit("query-changed", newQuery);
    });

    document.addEventListener("keydown", e => {
      if (!((window.navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey) && e.keyCode == 13)) {
        return;
      }

      if (!window.state.loggedIn) {
        return;
      }

      this.submitQuery();
    });

    this.setInputQuery();
  }
}
</script>

<style lang="scss">
.chart-sql-editor {
  &__header {
    padding-top: $px24;
  }

  &__container {
    display: flex;
    width: 100%;
  }

  &__menu {
  }

  &__data {
    flex-grow: 1;
    padding-left: $px24;
    margin-right: $px24;
    padding-top: 27px;
    padding-right: $px16;
    padding-bottom: $px48;
    min-height: 100vh;
    max-height: 100vh;
    overflow-y: auto;
    overflow-x: hidden;

    &::-webkit-scrollbar {
      width: $px8;
      height: $px8;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: $px4;

    	background-color: $grey-8;
    }
  }

  &__config {
    width: 30%;
    min-width: $px384;
    overflow-y: scroll;
    padding-right: $px16;
    padding-bottom: $px48;

    &::-webkit-scrollbar {
      width: $px8;
      height: $px8;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: $px4;

    	background-color: $grey-8;
    }
  }

  &__query-editor {
    width: 100%;
    height: 250px;
    margin-bottom: $px8;

    border: $px1 solid $grey-9;
    border-radius: $px4;
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__database-name {
    display: flex;
    align-items: center;
    justify-content: flex-start;

    @include font-small;
    color: $grey-5;
    text-decoration: none;
  }

  &__database-name-icon {
    width: $px24;
    height: $px24;
    margin-left: $px4;
  }

  &__chart-editor {
    display: none;

    @include breakpoint($md) {
      display: block;
    }
  }

  &__tip {
    padding: $px16;

    border-radius: $px4;

    background-color: $grey-10;

    color: $grey-1;
  }
}
</style>
